import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import {
  AdminAlertText,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminListFilterDropdown,
  AdminFilterDropdownButton,
  AdminHeaderAction,
  AdminMetadataText,
  AdminPageTitle,
  AdminPanelTitle,
  AdminRowActionsButton,
  AdminSearchField,
  AdminStatusBadge,
  AdminSupportingText,
  AdminTabButton,
  AdminTableBody,
  AdminSortableTableHeader,
  AdminTableHeader,
  AdminTableMeta,
  AdminWidgetTitle,
  adminFilterDateInputClasses,
  AdminFilterChevron,
  AdminToolbarLabel,
  ADMIN_LIST_FILTER_MENU_CLASS,
  AdminRowMenuIcon,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';

import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import useClickOutside from '../../../Hooks/useClickOutside';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';

const SecurityIncidents = () => {
  const navigate = useNavigate();
  
  // Grid template columns constant for table alignment
  const gridTemplateColumns = 'minmax(120px, 1.2fr) minmax(150px, 1.5fr) minmax(100px, 1fr) minmax(250px, 2.5fr) minmax(200px, 2fr) minmax(100px, 1fr) minmax(120px, 1.2fr) minmax(120px, 1.2fr) minmax(120px, 1.2fr) auto';
  
  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState({
    severity: null, // Radio: null (All), 'Medium', 'High', 'Critical'
    type: null, // Radio: null (All), 'Account Takeover', 'Identity Fraud', etc.
    status: null, // Radio: null (All), 'Open', 'Investigating', 'Mitigated', 'Closed'
    owner: null, // Radio: null (All), Owner names
    dateFrom: '',
    dateTo: '',
    involvesLegal: null, // Radio: null (All), 'Yes', 'No'
    involvesLawEnforcement: null, // Radio: null (All), 'Yes', 'No'
    source: null, // Radio: null (All), 'Escalated from Fraud Alerts', 'Escalated from Moderation', 'Auto (Security rules)', 'Manual (Security/Legal)'
  });
  
  // Dropdown states
  const [severityOpen, setSeverityOpen] = useState(false);
  const [typeOpen, setTypeOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [ownerOpen, setOwnerOpen] = useState(false);
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [involvesLegalOpen, setInvolvesLegalOpen] = useState(false);
  const [involvesLawEnforcementOpen, setInvolvesLawEnforcementOpen] = useState(false);
  const [sourceOpen, setSourceOpen] = useState(false);
  const [rowActionsOpen, setRowActionsOpen] = useState({});
  
  // Refs for click outside
  const severityRef = useRef(null);
  const typeRef = useRef(null);
  const statusRef = useRef(null);
  const ownerRef = useRef(null);
  const dateRangeRef = useRef(null);
  const involvesLegalRef = useRef(null);
  const involvesLawEnforcementRef = useRef(null);
  const sourceRef = useRef(null);
  const rowActionsRefs = useRef({});
  
  // Click outside handlers
  useClickOutside(severityRef, () => setSeverityOpen(false));
  useClickOutside(typeRef, () => setTypeOpen(false));
  useClickOutside(statusRef, () => setStatusOpen(false));
  useClickOutside(ownerRef, () => setOwnerOpen(false));
  useClickOutside(dateRangeRef, () => setDateRangeOpen(false));
  useClickOutside(involvesLegalRef, () => setInvolvesLegalOpen(false));
  useClickOutside(involvesLawEnforcementRef, () => setInvolvesLawEnforcementOpen(false));
  useClickOutside(sourceRef, () => setSourceOpen(false));

  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFilteredCount, setTotalFilteredCount] = useState(0);
  const [sortField, setSortField] = useState('openedDate');
  const [sortOrder, setSortOrder] = useState('desc');

  // Severity options
  const severityOptions = ['Medium', 'High', 'Critical'];

  // Type options
  const typeOptions = [
    'Account Takeover',
    'Identity Fraud',
    'Payment Fraud / Chargeback Chain',
    'Large-scale Abuse / Bot / Device Farm',
    'Data Integrity Issue',
    'Data Leak / Security Breach Suspicion',
    'System Breach Attempt',
    'Legal / Regulatory Threat',
    'Other'
  ];

  // Status options
  const statusOptions = ['Open', 'Investigating', 'Mitigated', 'Closed'];

  // Owner options (mock security leads)
  const ownerOptions = ['Unassigned', 'David Lee (Security Lead)', 'Sarah Nguyen (Fraud Lead)', 'Michael Chen (Legal)', 'Emma Wilson (Security)'];

  // Source options
  const sourceOptions = [
    'Escalated from Fraud Alerts',
    'Escalated from Moderation',
    'Auto (Security rules)',
    'Manual (Security/Legal)'
  ];

  // Severity badge color
  // Status badge color
  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Search helper function
  const matchesSearchQuery = (incident, searchTerm) => {
    if (!searchTerm || searchTerm.trim().length === 0) {
      return true;
    }

    const term = searchTerm.toLowerCase().trim();
    
    const incidentIdMatch = incident.id && incident.id.toLowerCase().includes(term);
    const titleMatch = incident.title && incident.title.toLowerCase().includes(term);
    const businessNameMatch = incident.businessName && incident.businessName.toLowerCase().includes(term);
    const customerNameMatch = incident.customerName && incident.customerName.toLowerCase().includes(term);
    const emailMatch = incident.email && incident.email.toLowerCase().includes(term);
    const abnMatch = incident.abn && incident.abn.replace(/\s/g, '').includes(term.replace(/\s/g, ''));
    
    return incidentIdMatch || titleMatch || businessNameMatch || customerNameMatch || emailMatch || abnMatch;
  };

  // Fixed dummy incidents data
  const generateDummyIncidents = () => {
    const now = new Date();
    const incidents = [];
    
    const fixedIncidents = [
      { incidentNum: 1, type: 'Account Takeover', severity: 'Critical', status: 'Open', owner: 'David Lee (Security Lead)', title: 'ATO cluster via credential stuffing – 18 accounts', customers: 18, businesses: 6, fraudCases: 4, daysAgo: 2, involvesLegal: false, involvesLawEnforcement: true, source: 'Auto (Security rules)', chargebackExposure: 12500 },
      { incidentNum: 2, type: 'Payment Fraud / Chargeback Chain', severity: 'High', status: 'Investigating', owner: 'Sarah Nguyen (Fraud Lead)', title: 'Chargeback ring targeting plumbing businesses', customers: 12, businesses: 8, fraudCases: 6, daysAgo: 5, involvesLegal: true, involvesLawEnforcement: false, source: 'Escalated from Fraud Alerts', chargebackExposure: 38500 },
      { incidentNum: 3, type: 'Identity Fraud', severity: 'High', status: 'Open', owner: 'David Lee (Security Lead)', title: 'Forged licences reused across 5 ABNs', customers: 5, businesses: 5, fraudCases: 3, daysAgo: 1, involvesLegal: true, involvesLawEnforcement: false, source: 'Escalated from Moderation', chargebackExposure: 0 },
      { incidentNum: 4, type: 'Large-scale Abuse / Bot / Device Farm', severity: 'Critical', status: 'Investigating', owner: 'Emma Wilson (Security)', title: 'Device farm detected – 45 automated registrations', customers: 45, businesses: 12, fraudCases: 8, daysAgo: 3, involvesLegal: false, involvesLawEnforcement: false, source: 'Auto (Security rules)', chargebackExposure: 0 },
      { incidentNum: 5, type: 'Data Leak / Security Breach Suspicion', severity: 'Critical', status: 'Open', owner: 'David Lee (Security Lead)', title: 'Suspected credential database exposure', customers: 0, businesses: 0, fraudCases: 0, daysAgo: 1, involvesLegal: true, involvesLawEnforcement: true, source: 'Manual (Security/Legal)', chargebackExposure: 0 },
      { incidentNum: 6, type: 'System Breach Attempt', severity: 'High', status: 'Mitigated', owner: 'Michael Chen (Legal)', title: 'Credential stuffing attack on admin panel', customers: 0, businesses: 0, fraudCases: 0, daysAgo: 8, involvesLegal: false, involvesLawEnforcement: false, source: 'Auto (Security rules)', chargebackExposure: 0 },
      { incidentNum: 7, type: 'Legal / Regulatory Threat', severity: 'High', status: 'Open', owner: 'Michael Chen (Legal)', title: 'Fair Trading investigation – multiple complaints', customers: 8, businesses: 3, fraudCases: 2, daysAgo: 4, involvesLegal: true, involvesLawEnforcement: false, source: 'Manual (Security/Legal)', chargebackExposure: 8500 },
      { incidentNum: 8, type: 'Payment Fraud / Chargeback Chain', severity: 'Medium', status: 'Closed', owner: 'Sarah Nguyen (Fraud Lead)', title: 'Small chargeback pattern – resolved', customers: 3, businesses: 2, fraudCases: 1, daysAgo: 15, involvesLegal: false, involvesLawEnforcement: false, source: 'Escalated from Fraud Alerts', chargebackExposure: 2200 },
      { incidentNum: 9, type: 'Account Takeover', severity: 'High', status: 'Investigating', owner: 'David Lee (Security Lead)', title: 'Suspected ATO on 6 customer accounts', customers: 6, businesses: 0, fraudCases: 2, daysAgo: 6, involvesLegal: false, involvesLawEnforcement: false, source: 'Auto (Security rules)', chargebackExposure: 0 },
      { incidentNum: 10, type: 'Data Integrity Issue', severity: 'Medium', status: 'Open', owner: 'Emma Wilson (Security)', title: 'Document verification system compromise', customers: 0, businesses: 15, fraudCases: 5, daysAgo: 7, involvesLegal: false, involvesLawEnforcement: false, source: 'Escalated from Moderation', chargebackExposure: 0 },
      { incidentNum: 11, type: 'Large-scale Abuse / Bot / Device Farm', severity: 'High', status: 'Mitigated', owner: 'Sarah Nguyen (Fraud Lead)', title: 'Bot network creating fake reviews', customers: 28, businesses: 10, fraudCases: 7, daysAgo: 12, involvesLegal: false, involvesLawEnforcement: false, source: 'Auto (Security rules)', chargebackExposure: 0 },
      { incidentNum: 12, type: 'Identity Fraud', severity: 'Critical', status: 'Investigating', owner: 'David Lee (Security Lead)', title: 'Mass identity theft – 32 accounts compromised', customers: 32, businesses: 8, fraudCases: 9, daysAgo: 2, involvesLegal: true, involvesLawEnforcement: true, source: 'Manual (Security/Legal)', chargebackExposure: 45000 },
    ];

    // Convert fixed data to incident objects
    fixedIncidents.forEach((incidentData, index) => {
      const openedDate = new Date(now);
      openedDate.setDate(openedDate.getDate() - incidentData.daysAgo);
      openedDate.setHours(9 + (index % 12), 11 + (index % 50), 0, 0);
      
      const lastActivity = new Date(openedDate);
      lastActivity.setDate(lastActivity.getDate() + Math.floor((index % 7) + 1));
      lastActivity.setHours(10 + (index % 10), 20 + (index % 40), 0, 0);

      incidents.push({
        id: `INC-${String(incidentData.incidentNum).padStart(4, '0')}`,
        type: incidentData.type,
        severity: incidentData.severity,
        status: incidentData.status,
        title: incidentData.title,
        owner: incidentData.owner,
        openedDate: openedDate.toISOString(),
        lastActivity: lastActivity.toISOString(),
        affectedEntities: {
          customers: incidentData.customers,
          businesses: incidentData.businesses,
          fraudCases: incidentData.fraudCases
        },
        involvesLegal: incidentData.involvesLegal,
        involvesLawEnforcement: incidentData.involvesLawEnforcement,
        source: incidentData.source,
        chargebackExposure: incidentData.chargebackExposure
      });
    });
    
    return incidents;
  };

  // Fetch incidents data
  useEffect(() => {
    const fetchIncidents = async () => {
      setLoading(true);
      setError(null);
      try {
        // TODO: Replace with actual API call
        await new Promise(resolve => setTimeout(resolve, 500));
        const allIncidents = generateDummyIncidents();

        // Apply search and filters
        let filteredIncidents = allIncidents.filter(incident => {
          // Search
          const matchesSearch = matchesSearchQuery(incident, debouncedSearch);

          // Severity filter
          const matchesSeverity = filters.severity === null || incident.severity === filters.severity;

          // Type filter
          const matchesType = filters.type === null || incident.type === filters.type;

          // Status filter
          const matchesStatus = filters.status === null || incident.status === filters.status;

          // Owner filter
          const matchesOwner = filters.owner === null || incident.owner === filters.owner;

          // Date range filter
          let matchesDateRange = true;
          if (filters.dateFrom || filters.dateTo) {
            const openedDate = new Date(incident.openedDate);
            if (filters.dateFrom) {
              const fromDate = new Date(filters.dateFrom);
              fromDate.setHours(0, 0, 0, 0);
              if (openedDate < fromDate) matchesDateRange = false;
            }
            if (filters.dateTo) {
              const toDate = new Date(filters.dateTo);
              toDate.setHours(23, 59, 59, 999);
              if (openedDate > toDate) matchesDateRange = false;
            }
          }

          // Involves Legal filter
          const matchesInvolvesLegal = filters.involvesLegal === null || 
            (filters.involvesLegal === 'Yes' && incident.involvesLegal) ||
            (filters.involvesLegal === 'No' && !incident.involvesLegal);

          // Involves Law Enforcement filter
          const matchesInvolvesLawEnforcement = filters.involvesLawEnforcement === null || 
            (filters.involvesLawEnforcement === 'Yes' && incident.involvesLawEnforcement) ||
            (filters.involvesLawEnforcement === 'No' && !incident.involvesLawEnforcement);

          // Source filter
          const matchesSource = filters.source === null || incident.source === filters.source;

          return matchesSearch && matchesSeverity && matchesType && matchesStatus && 
                 matchesOwner && matchesDateRange && matchesInvolvesLegal && 
                 matchesInvolvesLawEnforcement && matchesSource;
        });

        // Sort
        filteredIncidents.sort((a, b) => {
          let aValue = a[sortField];
          let bValue = b[sortField];

          if (sortField === 'openedDate' || sortField === 'lastActivity') {
            aValue = new Date(aValue).getTime();
            bValue = new Date(bValue).getTime();
          }

          if (aValue === null || aValue === undefined) return sortOrder === 'asc' ? 1 : -1;
          if (bValue === null || bValue === undefined) return sortOrder === 'asc' ? -1 : 1;

          if (typeof aValue === 'string' && typeof bValue === 'string') {
            return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
          }
          return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
        });

        const totalCount = filteredIncidents.length;
        const computedTotalPages = Math.max(1, Math.ceil(totalCount / CONFIG_LIST_PAGE_SIZE));
        const clampedPage = Math.min(currentPage, computedTotalPages);
        const startIndex = (clampedPage - 1) * CONFIG_LIST_PAGE_SIZE;

        setTotalFilteredCount(totalCount);
        setTotalPages(computedTotalPages);
        setIncidents(filteredIncidents.slice(startIndex, startIndex + CONFIG_LIST_PAGE_SIZE));

        if (clampedPage !== currentPage) {
          setCurrentPage(clampedPage);
        }
      } catch (err) {
        setError(err.message || 'Failed to load security incidents');
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };
    fetchIncidents();
  }, [debouncedSearch, filters, currentPage, sortField, sortOrder]);

  // Handle filter change
  const handleFilterChange = (filterName, value) => {
    if (filterName === 'severity' || filterName === 'type' || filterName === 'status' || 
        filterName === 'owner' || filterName === 'involvesLegal' || filterName === 'involvesLawEnforcement' || 
        filterName === 'source') {
      setFilters(prev => ({ 
        ...prev, 
        [filterName]: prev[filterName] === value ? null : value 
      }));
    } else if (filterName === 'dateFrom' || filterName === 'dateTo') {
      setFilters(prev => ({ ...prev, [filterName]: value }));
    }
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // Format date and time
  const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const dateLabel = date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    const timeLabel = date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
    return `${dateLabel} · ${timeLabel}`;
  };

  // Format affected entities
  const formatAffectedEntities = (affectedEntities) => {
    if (!affectedEntities) return '—';
    const parts = [];
    if (affectedEntities.customers > 0) parts.push(`Customers: ${affectedEntities.customers}`);
    if (affectedEntities.businesses > 0) parts.push(`Businesses: ${affectedEntities.businesses}`);
    if (affectedEntities.fraudCases > 0) parts.push(`Fraud Cases: ${affectedEntities.fraudCases}`);
    return parts.length > 0 ? parts.join(' · ') : '—';
  };

  // Toggle row actions dropdown
  const toggleRowActions = (incidentId) => {
    setRowActionsOpen((prev) => {
      if (prev[incidentId]) return {};
      return { [incidentId]: true };
    });
  };

  // Handle row action clicks
  const handleRowAction = (action, incident) => {
    setRowActionsOpen(prev => ({ ...prev, [incident.id]: false }));
    
    switch (action) {
      case 'openIncident':
        navigate(`/admin/fraud/incidents/${incident.id}`, { state: { incident } });
        break;
      case 'assign':
        const newOwner = window.prompt('Assign to (owner name or "Unassigned"):', incident.owner);
        if (newOwner !== null) {
          // TODO: Implement assign API call
          console.log('Assign incident:', incident.id, 'to', newOwner);
        }
        break;
      case 'changeStatus':
        const newStatus = window.prompt('Change status to (Open, Investigating, Mitigated, Closed):', incident.status);
        if (newStatus && ['Open', 'Investigating', 'Mitigated', 'Closed'].includes(newStatus)) {
          if (window.confirm(`Change status from ${incident.status} to ${newStatus}?`)) {
            // TODO: Implement status change API call
            console.log('Change status:', incident.id, 'to', newStatus);
          }
        }
        break;
      default:
        break;
    }
  };

  // Handle clicking outside to close row actions dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(rowActionsRefs.current).every(ref => {
        const el = ref;
        return !el || !el.contains(event.target);
      });

      if (isOutsideAll) {
        setRowActionsOpen({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const effectivePage = Math.min(currentPage, totalPages);
  const rangeStart =
    totalFilteredCount === 0 ? 0 : (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE + 1;
  const rangeEnd = Math.min(effectivePage * CONFIG_LIST_PAGE_SIZE, totalFilteredCount);
  const showPagination = totalFilteredCount > CONFIG_LIST_PAGE_SIZE;

  return (
    <AdminLayout>
      <div>

        <AdminPageTitle>Security Incidents</AdminPageTitle>

        {/* Search and Filters Row */}
        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            {/* Search - Left */}
            <AdminSearchField
              className="w-full max-w-[450px]"
              type="text"
              placeholder="Search Incident ID, title/summary, business name, customer name, email, ABN..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search security incidents"
            />

            {/* Filters - Right */}
            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              <AdminListFilterDropdown
                label="Severity"
                value={filters.severity}
                options={[
                  { value: null, label: 'All Severities' },
                  ...severityOptions.map((option) => ({ value: option, label: option })),
                ]}
                onChange={(value) => handleFilterChange('severity', value)}
                menuRef={severityRef}
                open={severityOpen}
                setOpen={setSeverityOpen}
              />
              <AdminListFilterDropdown
                label="Type"
                value={filters.type}
                options={[
                  { value: null, label: 'All Types' },
                  ...typeOptions.map((option) => ({ value: option, label: option })),
                ]}
                onChange={(value) => handleFilterChange('type', value)}
                menuRef={typeRef}
                open={typeOpen}
                setOpen={setTypeOpen}
              />
              <AdminListFilterDropdown
                label="Status"
                value={filters.status}
                options={[
                  { value: null, label: 'All Statuses' },
                  ...statusOptions.map((option) => ({ value: option, label: option })),
                ]}
                onChange={(value) => handleFilterChange('status', value)}
                menuRef={statusRef}
                open={statusOpen}
                setOpen={setStatusOpen}
              />
              <AdminListFilterDropdown
                label="Owner"
                value={filters.owner}
                options={[
                  { value: null, label: 'All Owners' },
                  ...ownerOptions.map((option) => ({ value: option, label: option })),
                ]}
                onChange={(value) => handleFilterChange('owner', value)}
                menuRef={ownerRef}
                open={ownerOpen}
                setOpen={setOwnerOpen}
              />
              <div className="relative" ref={dateRangeRef}>
                <AdminFilterDropdownButton
                  type="button"
                  onClick={() => setDateRangeOpen(!dateRangeOpen)}
                  aria-expanded={dateRangeOpen}
                  aria-label="Date Range"
                >
                  <span className="truncate">
                    {filters.dateFrom || filters.dateTo
                      ? [filters.dateFrom, filters.dateTo].filter(Boolean).join(' – ')
                      : 'All Date Ranges'}
                  </span>
                  <AdminFilterChevron open={dateRangeOpen} />
                </AdminFilterDropdownButton>
                {dateRangeOpen && (
                  <div className={`${ADMIN_LIST_FILTER_MENU_CLASS} !max-w-[18rem] px-3 py-2 space-y-2`}>
                    <div>
                      <AdminToolbarLabel className="block mb-1">From</AdminToolbarLabel>
                      <input
                        type="date"
                        value={filters.dateFrom}
                        onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                        className={adminFilterDateInputClasses()}
                      />
                    </div>
                    <div>
                      <AdminToolbarLabel className="block mb-1">To</AdminToolbarLabel>
                      <input
                        type="date"
                        value={filters.dateTo}
                        onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                        className={adminFilterDateInputClasses()}
                      />
                    </div>
                  </div>
                )}
              </div>
              <AdminListFilterDropdown
                label="Involves Legal"
                value={filters.involvesLegal}
                options={[
                  { value: null, label: 'All Legal' },
                  { value: 'Yes', label: 'Yes' },
                  { value: 'No', label: 'No' },
                ]}
                onChange={(value) => handleFilterChange('involvesLegal', value)}
                menuRef={involvesLegalRef}
                open={involvesLegalOpen}
                setOpen={setInvolvesLegalOpen}
              />
              <AdminListFilterDropdown
                label="Involves Law Enforcement"
                value={filters.involvesLawEnforcement}
                options={[
                  { value: null, label: 'All Law Enforcement' },
                  { value: 'Yes', label: 'Yes' },
                  { value: 'No', label: 'No' },
                ]}
                onChange={(value) => handleFilterChange('involvesLawEnforcement', value)}
                menuRef={involvesLawEnforcementRef}
                open={involvesLawEnforcementOpen}
                setOpen={setInvolvesLawEnforcementOpen}
              />
              <AdminListFilterDropdown
                label="Source"
                value={filters.source}
                options={[
                  { value: null, label: 'All Sources' },
                  ...sourceOptions.map((option) => ({ value: option, label: option })),
                ]}
                onChange={(value) => handleFilterChange('source', value)}
                menuRef={sourceRef}
                open={sourceOpen}
                setOpen={setSourceOpen}
              />
            </div>
          </div>
        </div>

        {/* Security Incidents Table */}
        <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
          {loading ? (
            // Loading skeleton
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-12 bg-white animate-pulse rounded"></div>
              ))}
            </div>
          ) : error ? (
            // Error state
            <div className="text-center py-10">
              <p className="text-alertred mb-4">{error}</p>
              <AdminHeaderAction variant={"primarysmall"} onClick={() => window.location.reload()}>
                Retry
              </AdminHeaderAction>
            </div>
          ) : incidents.length === 0 ? (
            // Empty state
            <div className="text-center py-10">
              <p className="text-mediumgray">No security incidents match your filters.</p>
            </div>
          ) : (
            // Table
            <>
              {/* Header */}
              <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns }}>
                <div className="flex items-center">
                  <AdminSortableTableHeader onClick={() => handleSort('id')}>Incident ID</AdminSortableTableHeader>
                </div>
                <div className="flex items-center">
                  <AdminTableHeader>Type</AdminTableHeader>
                </div>
                <div className="flex items-center">
                  <AdminSortableTableHeader onClick={() => handleSort('severity')}>Severity</AdminSortableTableHeader>
                </div>
                <div className="flex items-center">
                  <AdminTableHeader>Title / Short Summary</AdminTableHeader>
                </div>
                <div className="flex items-center">
                  <AdminTableHeader>Affected Entities</AdminTableHeader>
                </div>
                <div className="flex items-center">
                  <AdminSortableTableHeader onClick={() => handleSort('status')}>Status</AdminSortableTableHeader>
                </div>
                <div className="flex items-center">
                  <AdminSortableTableHeader onClick={() => handleSort('owner')}>Owner</AdminSortableTableHeader>
                </div>
                <div className="flex items-center">
                  <AdminSortableTableHeader onClick={() => handleSort('openedDate')}>Opened Date</AdminSortableTableHeader>
                </div>
                <div className="flex items-center">
                  <AdminSortableTableHeader onClick={() => handleSort('lastActivity')}>Last Activity</AdminSortableTableHeader>
                </div>
                <div className="flex items-center justify-center">
                  <AdminTableHeader>Actions</AdminTableHeader></div>
              </div>

              {/* Rows */}
              {incidents.map((incident, index) => (
                <div
                  key={incident.id || index}
                  className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 cursor-pointer gap-4"
                  style={{ gridTemplateColumns }}
                  onClick={() => navigate(`/admin/fraud/incidents/${incident.id}`, { state: { incident } })}
                >
                  {/* Incident ID */}
                  <div className="flex items-center">
                    <span className="">#{incident.id || '—'}</span>
                  </div>
                  
                  {/* Type */}
                  <div className="flex items-center min-w-0">
                    <span className="truncate" title={incident.type}>{incident.type}</span>
                  </div>
                  
                  {/* Severity */}
                  <div className="flex items-center">
                    <AdminStatusBadge status={incident.severity} label={incident.severity} />
                  </div>
                  
                  {/* Title / Short Summary */}
                  <div className="flex items-center min-w-0">
                    <span className="truncate" title={incident.title}>{incident.title}</span>
                  </div>
                  
                  {/* Affected Entities */}
                  <div className="flex items-center min-w-0">
                    <span className="truncate" title={formatAffectedEntities(incident.affectedEntities)}>
                      {formatAffectedEntities(incident.affectedEntities)}
                    </span>
                  </div>
                  
                  {/* Status */}
                  <div className="flex items-center">
                    <AdminStatusBadge status={incident.status} label={incident.status} />
                  </div>
                  
                  {/* Owner */}
                  <div className="flex items-center">
                    <span className="">{incident.owner || '—'}</span>
                  </div>
                  
                  {/* Opened Date */}
                  <div className="flex items-center">
                    <span className="">{formatDateTime(incident.openedDate)}</span>
                  </div>
                  
                  {/* Last Activity */}
                  <div className="flex items-center">
                    <span className="">{formatDateTime(incident.lastActivity)}</span>
                  </div>
                  
                  {/* Row Actions */}
                  <div className="flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
                    <div className="relative inline-flex" ref={el => rowActionsRefs.current[incident.id] = el}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRowActions(incident.id);
                        }}
                        className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                        title="Row Actions"
                      >
                        <AdminRowMenuIcon />
                      </button>
                      {rowActionsOpen[incident.id] && (
                        <div className="absolute right-0 top-full mt-1 bg-white rounded-lg z-30 min-w-[200px] shadow-lg border border-lightgray">
                          <div className="py-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRowAction('openIncident', incident);
                              }}
                              className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                            >
                              <AdminSupportingText>Open Incident</AdminSupportingText>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRowAction('assign', incident);
                              }}
                              className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                            >
                              <AdminSupportingText>Assign / Reassign</AdminSupportingText>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRowAction('changeStatus', incident);
                              }}
                              className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                            >
                              <AdminSupportingText>Change Status</AdminSupportingText>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={!loading && !error && showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalFilteredCount}
          currentPage={effectivePage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default SecurityIncidents;

